/* eslint-disable @typescript-eslint/no-unused-vars */
import firebase from 'firebase/app';
import { CourseData, CourseLevel, DayOfTheWeek, Field, SectionInfo, TimeZone } from './courseData';
import {
  FirebaseCourseData,
  FirebaseCourseLevel,
  FirebaseField,
  FirebaseSectionInfo,
  FirebaseTimeZone,
} from '@ypp-portal/models/course/firebaseCourseData';
import { mapEnums } from '../util/mapEnums';
import { ApprovalInfo } from './approval/approval';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);

const { original: fieldMap, reverse: reverseFieldMap } = mapEnums(Field, FirebaseField);
const { original: courseLevelMap, reverse: reverseCourseLevelMap } = mapEnums(
  CourseLevel,
  FirebaseCourseLevel
);
const { original: timeZoneMap, reverse: reverseTimeZoneMap } = mapEnums(TimeZone, FirebaseTimeZone);

const dayMap: Record<DayOfTheWeek, number> = {
  [DayOfTheWeek.Monday]: 0,
  [DayOfTheWeek.Tuesday]: 1,
  [DayOfTheWeek.Wednesday]: 2,
  [DayOfTheWeek.Thursday]: 3,
  [DayOfTheWeek.Friday]: 4,
  [DayOfTheWeek.Saturday]: 5,
  [DayOfTheWeek.Sunday]: 6,
};

const reverseDayMap: Record<number, DayOfTheWeek> = {
  0: DayOfTheWeek.Monday,
  1: DayOfTheWeek.Tuesday,
  2: DayOfTheWeek.Wednesday,
  3: DayOfTheWeek.Thursday,
  4: DayOfTheWeek.Friday,
  5: DayOfTheWeek.Saturday,
  6: DayOfTheWeek.Sunday,
};

// From https://fettblog.eu/typescript-match-the-exact-object-shape/
type IfExact<T, Shape> = T extends Shape
  ? Exclude<keyof T, keyof Shape> extends never
    ? T
    : never
  : never;

export class CourseDataConverter implements firebase.firestore.FirestoreDataConverter<CourseData> {
  toFirestore(
    modelObject: Partial<CourseData>
  ): IfExact<typeof returnObject, Partial<FirebaseCourseData>> {
    // Take out some properties to play nice with typescript
    const {
      field,
      courseLevel,
      sections,
      courseApprovalRef,
      curriculumApprovalRef,
      ...modelShallowCopy
    } = { ...modelObject };

    const returnObject: typeof modelShallowCopy & Partial<FirebaseCourseData> = modelShallowCopy;

    if (modelObject.field) {
      returnObject.field = fieldMap[modelObject.field];
    }

    if (modelObject.courseLevel) {
      returnObject.courseLevel = courseLevelMap[modelObject.courseLevel];
    }

    if (modelObject.sections) {
      returnObject.sections = this.convertSectionsToFirebase(modelObject.sections);
    }

    return returnObject;
  }

  fromFirestore(
    snapshot: firebase.firestore.QueryDocumentSnapshot<FirebaseCourseData>
  ): CourseData {
    const data = snapshot.data();
    return {
      ...data,
      name: data.name ?? '',
      field: reverseFieldMap[data.field ?? FirebaseField.Arts],
      zoomLink: data.zoomLink ?? '',
      description: data.description ?? '',
      prereqNotes: data.prereqNotes ?? '',
      studentCountInfo: data.studentCountInfo ?? { recommended: -1, max: -1 },
      gradeInfo: data.gradeInfo ?? {
        regular: { min: -1, max: -1 },
        exception: { min: -1, max: -1 },
      },
      courseLevel: reverseCourseLevelMap[data.courseLevel ?? FirebaseCourseLevel.OneHundred],
      sections: this.convertSectionsFromFirebase(data.sections ?? []),
      curriculumFiles: data.curriculumFiles ?? [],
      courseApprovalRef: snapshot.ref
        .collection('admin')
        .doc('courseApproval') as firebase.firestore.DocumentReference<ApprovalInfo>,
      curriculumApprovalRef: snapshot.ref
        .collection('admin')
        .doc('curriculumApproval') as firebase.firestore.DocumentReference<ApprovalInfo>,
    };
  }

  private convertSectionsToFirebase(sections: SectionInfo[]): FirebaseSectionInfo[] {
    console.log(sections);
    return sections.map((section) => ({
      timeZone: timeZoneMap[section.timeZone],
      meetings: section.meetings.map((meeting) => {
        const day = dayMap[meeting.day];
        const { hour, minute } = this.parseTime(meeting.time);
        return {
          minutesFromWeekStart: this.calculateMinutesFromWeekStart(day, hour, minute),
        };
      }),
    }));
  }

  private calculateMinutesFromWeekStart(day: number, hour: number, minute: number): number {
    return dayjs.duration({ days: day, hours: hour, minutes: minute }).asMinutes();
  }

  private parseTime(time_string: string) {
    const time = dayjs(time_string, 'HH:mm');
    return { hour: time.hour(), minute: time.minute() };
  }

  private convertSectionsFromFirebase(sections: FirebaseSectionInfo[]): SectionInfo[] {
    return sections.map((section) => ({
      timeZone: reverseTimeZoneMap[section.timeZone],
      meetings: section.meetings.map((meeting) => {
        // Not using startOf('week') because I'm not sure if it's locale dependant or not, if you can figure it out please confirm
        const date = dayjs(0)
          .day(0)
          .hour(0)
          .minute(0)
          .add(dayjs.duration({ minutes: meeting.minutesFromWeekStart }));
        return {
          day: reverseDayMap[date.day()],
          time: date.format('HH:mm'),
        };
      }),
    }));
  }
}
